import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import {
  type CreateHttpEndpointRequest,
  DEFAULT_LOGIN_CONFIG,
  type HttpAuthType,
  type HttpMethod,
  type UpdateHttpEndpointRequest,
} from '@innermate/shared';
import { type ReactNode, useState } from 'react';

interface RowProps {
  label: string;
  description?: string;
  required?: boolean;
  children: ReactNode;
}

function Row({ label, description, required, children }: RowProps) {
  return (
    <div className="space-y-1.5">
      <div className="text-sm font-medium">
        {label}
        {required ? <span className="text-destructive"> *</span> : null}
      </div>
      {children}
      {description ? <p className="text-xs text-foreground-muted">{description}</p> : null}
    </div>
  );
}

type Mode = 'create' | 'edit';

interface InitialValues {
  name: string;
  baseUrl: string;
  description: string | null;
  authType: HttpAuthType;
  basicUsername: string | null;
  loginUsername: string | null;
  loginConfig: typeof DEFAULT_LOGIN_CONFIG;
  cookieMode: boolean;
  allowedMethods: HttpMethod[];
  pathAllowPatterns: string[];
  pathDenyPatterns: string[];
  defaultHeaders: Record<string, string>;
  requestTimeoutMs: number;
  maxResponseBytes: number;
  hasBearerToken?: boolean;
  hasBasicPassword?: boolean;
  hasLoginPassword?: boolean;
}

const DEFAULT: InitialValues = {
  name: '',
  baseUrl: '',
  description: null,
  authType: 'none',
  basicUsername: null,
  loginUsername: null,
  loginConfig: { ...DEFAULT_LOGIN_CONFIG },
  cookieMode: false,
  allowedMethods: ['GET'],
  pathAllowPatterns: [],
  pathDenyPatterns: [],
  defaultHeaders: {},
  requestTimeoutMs: 10000,
  maxResponseBytes: 65536,
};

const AUTH_OPTIONS: Array<{
  value: HttpAuthType;
  label: string;
  description: string;
}> = [
  { value: 'none', label: '없음', description: '인증 헤더 없이 호출.' },
  {
    value: 'bearer_static',
    label: 'Bearer 고정',
    description: 'Admin 이 저장한 장기 토큰을 Authorization: Bearer 로 주입.',
  },
  {
    value: 'basic',
    label: 'Basic',
    description: 'username / password 를 base64 Authorization: Basic 로 주입.',
  },
  {
    value: 'login_form',
    label: 'Login 자동',
    description: '로그인 엔드포인트 호출 → 응답 토큰을 캐시해 후속 요청에 재주입.',
  },
];

const ALL_METHODS: HttpMethod[] = ['GET', 'POST', 'PUT', 'DELETE', 'PATCH'];

interface HttpEndpointFormProps {
  mode: Mode;
  initial?: InitialValues;
  submitLabel: string;
  submitting?: boolean;
  serverError?: string | null;
  onSubmit: (body: CreateHttpEndpointRequest | UpdateHttpEndpointRequest) => void;
  onCancel?: () => void;
}

export function HttpEndpointForm({
  mode,
  initial,
  submitLabel,
  submitting,
  serverError,
  onSubmit,
  onCancel,
}: HttpEndpointFormProps) {
  const [v, setV] = useState<InitialValues>(initial ?? DEFAULT);
  const [bearerToken, setBearerToken] = useState('');
  const [basicPassword, setBasicPassword] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [headersJson, setHeadersJson] = useState(() => JSON.stringify(v.defaultHeaders, null, 2));
  const [bodyTemplateJson, setBodyTemplateJson] = useState(() =>
    JSON.stringify(v.loginConfig.bodyTemplate, null, 2),
  );
  const [jsonError, setJsonError] = useState<string | null>(null);

  function patch<K extends keyof InitialValues>(key: K, value: InitialValues[K]): void {
    setV((prev) => ({ ...prev, [key]: value }));
  }

  function patchLoginConfig<K extends keyof InitialValues['loginConfig']>(
    key: K,
    value: InitialValues['loginConfig'][K],
  ): void {
    setV((prev) => ({
      ...prev,
      loginConfig: { ...prev.loginConfig, [key]: value },
    }));
  }

  function toggleMethod(m: HttpMethod): void {
    const next = v.allowedMethods.includes(m)
      ? v.allowedMethods.filter((x) => x !== m)
      : [...v.allowedMethods, m];
    if (next.length === 0) return;
    patch('allowedMethods', next);
  }

  function handleSubmit(e: React.FormEvent): void {
    e.preventDefault();
    setJsonError(null);

    let parsedHeaders: Record<string, string>;
    try {
      parsedHeaders = headersJson.trim() === '' ? {} : JSON.parse(headersJson);
    } catch {
      setJsonError('기본 헤더가 올바른 JSON 이 아닙니다.');
      return;
    }
    let parsedBodyTemplate: Record<string, unknown>;
    try {
      parsedBodyTemplate = bodyTemplateJson.trim() === '' ? {} : JSON.parse(bodyTemplateJson);
    } catch {
      setJsonError('Login body template 이 올바른 JSON 이 아닙니다.');
      return;
    }

    const body: CreateHttpEndpointRequest = {
      name: v.name,
      baseUrl: v.baseUrl,
      description: v.description,
      authType: v.authType,
      basicUsername: v.basicUsername,
      loginUsername: v.loginUsername,
      loginConfig: { ...v.loginConfig, bodyTemplate: parsedBodyTemplate },
      cookieMode: v.cookieMode,
      allowedMethods: v.allowedMethods,
      pathAllowPatterns: v.pathAllowPatterns,
      pathDenyPatterns: v.pathDenyPatterns,
      defaultHeaders: parsedHeaders,
      requestTimeoutMs: v.requestTimeoutMs,
      maxResponseBytes: v.maxResponseBytes,
    };
    if (v.authType === 'bearer_static' && bearerToken !== '') body.bearerToken = bearerToken;
    if (v.authType === 'basic' && basicPassword !== '') body.basicPassword = basicPassword;
    if (v.authType === 'login_form' && loginPassword !== '') body.loginPassword = loginPassword;

    onSubmit(body);
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6 max-w-2xl">
      {serverError ? (
        <div
          role="alert"
          className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive"
        >
          {serverError}
        </div>
      ) : null}
      {jsonError ? (
        <div
          role="alert"
          className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive"
        >
          {jsonError}
        </div>
      ) : null}

      <Row label="이름" required>
        <Input
          value={v.name}
          onChange={(e) => patch('name', e.target.value)}
          placeholder="예: 사내 인사시스템"
          required
        />
      </Row>

      <Row
        label="Base URL"
        required
        description="엔드포인트 루트. 경로 포함하지 말고 origin 까지만. 예: https://api.example.com"
      >
        <Input
          type="url"
          value={v.baseUrl}
          onChange={(e) => patch('baseUrl', e.target.value)}
          placeholder="https://api.example.com"
          required
        />
      </Row>

      <Row label="설명" description="LLM 에 노출되어 언제 이 endpoint 를 사용할지 판단에 쓰입니다.">
        <textarea
          value={v.description ?? ''}
          onChange={(e) => patch('description', e.target.value || null)}
          rows={3}
          className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm"
          placeholder="예: 사내 인사시스템. /users, /users/:id 에서 직원 정보 조회."
        />
      </Row>

      <Row label="허용 메서드" required>
        <div className="flex flex-wrap gap-2">
          {ALL_METHODS.map((m) => {
            const on = v.allowedMethods.includes(m);
            return (
              <label
                key={m}
                className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs cursor-pointer ${
                  on ? 'border-accent/50 bg-accent/10' : 'border-border'
                }`}
              >
                <input
                  type="checkbox"
                  checked={on}
                  onChange={() => toggleMethod(m)}
                  className="size-3.5 accent-accent"
                />
                <span className="font-mono">{m}</span>
              </label>
            );
          })}
        </div>
      </Row>

      <Row label="인증 방식">
        <div className="space-y-2">
          {AUTH_OPTIONS.map((opt) => (
            <label
              key={opt.value}
              className="flex items-start gap-2 p-2 rounded-md border border-border hover:bg-surface-muted/30 cursor-pointer"
            >
              <input
                type="radio"
                name="authType"
                value={opt.value}
                checked={v.authType === opt.value}
                onChange={() => patch('authType', opt.value)}
                className="mt-0.5"
              />
              <div>
                <div className="text-sm font-medium">{opt.label}</div>
                <div className="text-xs text-foreground-muted">{opt.description}</div>
              </div>
            </label>
          ))}
        </div>
      </Row>

      {v.authType === 'bearer_static' ? (
        <Row
          label="Bearer 토큰"
          description={
            mode === 'edit' && initial?.hasBearerToken
              ? '저장된 토큰이 있습니다. 비워두면 유지, 입력하면 교체됩니다.'
              : undefined
          }
        >
          <PasswordInput
            value={bearerToken}
            onChange={(e) => setBearerToken(e.target.value)}
            placeholder="eyJhbGciOi..."
          />
        </Row>
      ) : null}

      {v.authType === 'basic' ? (
        <>
          <Row label="Basic 사용자명">
            <Input
              value={v.basicUsername ?? ''}
              onChange={(e) => patch('basicUsername', e.target.value || null)}
            />
          </Row>
          <Row
            label="Basic 비밀번호"
            description={
              mode === 'edit' && initial?.hasBasicPassword
                ? '저장된 비밀번호가 있습니다. 비워두면 유지, 입력하면 교체됩니다.'
                : undefined
            }
          >
            <PasswordInput
              value={basicPassword}
              onChange={(e) => setBasicPassword(e.target.value)}
            />
          </Row>
        </>
      ) : null}

      {v.authType === 'login_form' ? (
        <>
          <Row label="Login 계정 username">
            <Input
              value={v.loginUsername ?? ''}
              onChange={(e) => patch('loginUsername', e.target.value || null)}
            />
          </Row>
          <Row
            label="Login 계정 password"
            description={
              mode === 'edit' && initial?.hasLoginPassword
                ? '저장된 비밀번호가 있습니다. 비워두면 유지, 입력하면 교체됩니다.'
                : undefined
            }
          >
            <PasswordInput
              value={loginPassword}
              onChange={(e) => setLoginPassword(e.target.value)}
            />
          </Row>

          <div className="rounded-lg border border-border bg-surface-muted/20 p-4 space-y-4">
            <div className="text-sm font-medium">Login 호출 설정</div>
            <Row label="Login path" required>
              <Input
                value={v.loginConfig.path}
                onChange={(e) => patchLoginConfig('path', e.target.value)}
                placeholder="/auth/login"
              />
            </Row>
            <Row label="Login method">
              <select
                value={v.loginConfig.method}
                onChange={(e) => patchLoginConfig('method', e.target.value as HttpMethod)}
                className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm"
              >
                {ALL_METHODS.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            </Row>
            <Row
              label="Login body template (JSON)"
              description="{{username}} / {{password}} 문자열은 저장된 계정 정보로 치환됩니다."
            >
              <textarea
                value={bodyTemplateJson}
                onChange={(e) => setBodyTemplateJson(e.target.value)}
                rows={6}
                className="w-full rounded-md border border-border bg-surface px-3 py-2 font-mono text-xs"
                placeholder={'{\n  "username": "{{username}}",\n  "password": "{{password}}"\n}'}
              />
            </Row>
            <Row
              label="Token JSON path"
              description="로그인 응답에서 추출할 필드. 예: accessToken 또는 data.token"
            >
              <Input
                value={v.loginConfig.tokenJsonPath}
                onChange={(e) => patchLoginConfig('tokenJsonPath', e.target.value)}
              />
            </Row>
            <Row label="Token header name">
              <Input
                value={v.loginConfig.tokenHeaderName}
                onChange={(e) => patchLoginConfig('tokenHeaderName', e.target.value)}
              />
            </Row>
            <Row label="Token prefix">
              <Input
                value={v.loginConfig.tokenPrefix}
                onChange={(e) => patchLoginConfig('tokenPrefix', e.target.value)}
              />
            </Row>
            <Row label="세션 TTL (초)" description="이 시간이 지나면 자동 재로그인.">
              <Input
                type="number"
                min={30}
                max={86400}
                value={v.loginConfig.sessionTtlSec}
                onChange={(e) => patchLoginConfig('sessionTtlSec', Number(e.target.value) || 1800)}
              />
            </Row>
          </div>
        </>
      ) : null}

      <Row label="쿠키 세션 사용">
        <label className="inline-flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={v.cookieMode}
            onChange={(e) => patch('cookieMode', e.target.checked)}
            className="size-4 accent-accent"
          />
          Set-Cookie 를 저장해 후속 요청에 Cookie 헤더로 자동 전달
        </label>
      </Row>

      <details className="rounded-lg border border-border bg-surface">
        <summary className="px-3 py-2 text-sm font-medium cursor-pointer">고급 설정</summary>
        <div className="px-3 py-3 space-y-4">
          <Row
            label="Path allow 패턴"
            description="glob (예: /api/v1/*). 쉼표 구분. 비우면 전체 허용."
          >
            <Input
              value={v.pathAllowPatterns.join(', ')}
              onChange={(e) =>
                patch(
                  'pathAllowPatterns',
                  e.target.value
                    .split(',')
                    .map((s) => s.trim())
                    .filter(Boolean),
                )
              }
            />
          </Row>
          <Row label="Path deny 패턴" description="쉼표 구분.">
            <Input
              value={v.pathDenyPatterns.join(', ')}
              onChange={(e) =>
                patch(
                  'pathDenyPatterns',
                  e.target.value
                    .split(',')
                    .map((s) => s.trim())
                    .filter(Boolean),
                )
              }
            />
          </Row>
          <Row label="기본 헤더 (JSON)">
            <textarea
              value={headersJson}
              onChange={(e) => setHeadersJson(e.target.value)}
              rows={4}
              className="w-full rounded-md border border-border bg-surface px-3 py-2 font-mono text-xs"
              placeholder='{"Accept": "application/json"}'
            />
          </Row>
          <Row label="요청 timeout (ms)">
            <Input
              type="number"
              min={500}
              max={60000}
              value={v.requestTimeoutMs}
              onChange={(e) => patch('requestTimeoutMs', Number(e.target.value) || 10000)}
            />
          </Row>
          <Row label="최대 응답 크기 (bytes)">
            <Input
              type="number"
              min={1024}
              max={1048576}
              value={v.maxResponseBytes}
              onChange={(e) => patch('maxResponseBytes', Number(e.target.value) || 65536)}
            />
          </Row>
        </div>
      </details>

      <div className="flex items-center gap-2 pt-2 border-t border-border">
        <Button type="submit" disabled={submitting}>
          {submitting ? '저장 중…' : submitLabel}
        </Button>
        {onCancel ? (
          <Button type="button" variant="ghost" onClick={onCancel}>
            취소
          </Button>
        ) : null}
      </div>
    </form>
  );
}
